// Мастер «Новый объект». Панель, а не модалка: заведение объекта обратимо — лишний объект
// закрывается, а не остаётся навсегда. Модалка у нас оставлена под необратимое (правило 4),
// и разменивать её на обычную форму нельзя: тогда её перестанут читать там, где она важна.
//
// Мастер не уводит из раздела: список слева остаётся на экране, адрес панели живёт в ?p=,
// и ссылка на «раздел с открытым мастером» открывается у коллеги ровно так же.

import type { FormEvent } from 'react';
import { useState } from 'react';

import { RiBuilding4Line, RiErrorWarningLine } from '@remixicon/react';
import { useLocation, useNavigate } from 'react-router';

import type { ApiError } from '@/shared/errors/api-error';
import type { SiteIn } from '@/shared/api/endpoints/org';
import { useAuthStore } from '@/shared/auth/store';
import { can } from '@/shared/rbac/permissions';
import { parseSearch, popPanel } from '@/shared/routing/search';
import * as Alert from '@/shared/ui/alert';
import * as Button from '@/shared/ui/button';
import { ErrorAlert } from '@/shared/ui/error-alert';
import * as Hint from '@/shared/ui/hint';
import * as Input from '@/shared/ui/input';
import * as Label from '@/shared/ui/label';

import { NO_VALUE, SelectField } from '@/shared/ui/select-field';
import { TextField } from '@/shared/ui/text-field';
import {
  newIdempotencyKey,
  useContractsQuery,
  useCreateSiteMutation,
  useCustomersQuery,
  useLegalEntitiesQuery,
  useSiteTypesQuery,
} from '@/shared/org/queries';
import type { ТочкаКарты } from '../components/КартаОбъекта';
import { ЗонаНаКарте, РАДИУС_ПО_УМОЛЧАНИЮ } from '../components/ЗонаНаКарте';
import { ВыборСтаршего } from '../components/ВыборСтаршего';
import { NEW_SITE_BLANK, sitesHref } from '../route';
import { DEFAULT_TIMEZONE, SITE_TIMEZONES } from '../timezones';

/** Заведение объекта — право на правку справочников (server/app/api/v1/dictionaries.py). */
const EDIT_PERMISSION = 'core.dictionary.edit';

type FieldName = 'name' | 'customer_id' | 'legal_entity_id' | 'contract_id' | 'site_type_id';

export function NewSitePanel({ id, onClose }: { id: string; onClose: () => void }) {
  const navigate = useNavigate();
  const location = useLocation();
  const permissions = useAuthStore((state) => state.permissions);
  const allowed = can(permissions, EDIT_PERMISSION);

  // Ключ идемпотентности живёт столько же, сколько форма: двойное нажатие и повтор после
  // обрыва возвращают тот же объект, а не заводят второй.
  const [idempotencyKey] = useState(newIdempotencyKey);

  const [customerId, setCustomerId] = useState(id === NEW_SITE_BLANK ? NO_VALUE : id);
  const [legalEntityId, setLegalEntityId] = useState(NO_VALUE);
  const [siteTypeId, setSiteTypeId] = useState(NO_VALUE);
  const [contractId, setContractId] = useState(NO_VALUE);
  const [name, setName] = useState('');
  // Город, регион и адрес заводились ТОЛЬКО через сервер: в форме их не было, а объект без
  // города не попадал ни в отбор людей по региону, ни в отчёты по городам — и исправить это
  // было нечем, потому что правки объекта с экрана тоже не существовало (аудит 06.09.2026).
  const [city, setCity] = useState('');
  const [region, setRegion] = useState('');
  const [address, setAddress] = useState('');
  // Точка на карте и её зона. Без них четвёртая механика присутствия (тз 12.4.3) не строится
  // вовсе: телефону нечего сторожить, а завести координаты было негде — колонки в базе лежали
  // с первой миграции и не выходили ни в одну форму (аудит 06.09.2026).
  const [mapPoint, setMapPoint] = useState('');
  const [radius, setRadius] = useState(РАДИУС_ПО_УМОЛЧАНИЮ);
  const [polygon, setPolygon] = useState<ТочкаКарты[] | null>(null);
  const [patrol, setPatrol] = useState<number | null>(null);
  const [fieldManager, setFieldManager] = useState<string | null>(null);
  const [timezone, setTimezone] = useState(DEFAULT_TIMEZONE);
  const [errors, setErrors] = useState<Partial<Record<FieldName, string>>>({});
  const [gateNote, setGateNote] = useState<string | null>(null);

  const customers = useCustomersQuery({});
  const legalEntities = useLegalEntitiesQuery();
  const siteTypes = useSiteTypesQuery();
  const contracts = useContractsQuery(
    { customer_id: customerId === NO_VALUE ? undefined : customerId },
    customerId !== NO_VALUE,
  );
  const mutation = useCreateSiteMutation(idempotencyKey);

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!allowed) {
      setGateNote(
        'Заводить объекты может тот, у кого есть право на правку справочников. Попросите администратора выдать это право — форма при этом никуда не денется.',
      );
      return;
    }
    if (mutation.isPending) {
      setGateNote('Объект уже создаётся, ждём ответ сервера. Второй запрос не отправляем.');
      return;
    }

    const next: Partial<Record<FieldName, string>> = {};
    if (name.trim() === '') {
      next.name = 'Введите название объекта — по нему его будут искать в списке.';
    }
    if (customerId === NO_VALUE) {
      next.customer_id = 'Выберите заказчика: объект без заказчика не заводится.';
    }
    if (legalEntityId === NO_VALUE) {
      next.legal_entity_id = 'Выберите юрлицо: от него зависит нумерация ведомостей.';
    }
    if (Object.keys(next).length > 0) {
      setErrors(next);
      return;
    }

    setErrors({});
    setGateNote(null);
    const body: SiteIn = {
      name: name.trim(),
      customer_id: customerId,
      legal_entity_id: legalEntityId,
      site_type_id: siteTypeId === NO_VALUE ? null : siteTypeId,
      contract_id: contractId === NO_VALUE ? null : contractId,
      city: city.trim() === '' ? null : city.trim(),
      region: region.trim() === '' ? null : region.trim(),
      address: address.trim() === '' ? null : address.trim(),
      // Разбирает вставленное сервер: правило одно на кабинет, приложение и перенос данных.
      map_point: mapPoint.trim() === '' ? null : mapPoint.trim(),
      geofence_radius_m: radius,
      geofence_polygon: polygon,
      patrol_interval_hours: patrol,
      field_manager_employee_id: fieldManager,
      timezone,
    };
    mutation.mutate(body, {
      onSuccess: (site) => {
        // Панель уходит, а карточка нового объекта открывается сразу: человек видит
        // результат своего действия, а не возвращается в список искать его глазами.
        const search = popPanel(parseSearch(location.search));
        navigate(sitesHref('objects', { ...search, tab: undefined }, site.id));
      },
    });
  }

  return (
    <form onSubmit={handleSubmit} noValidate className='flex flex-col gap-3'>
      {!allowed && (
        <Alert.Root variant='light' status='warning' size='small' role='status'>
          <Alert.Icon as={RiErrorWarningLine} />
          <span>
            Права на правку справочников у вас нет: форму можно заполнить, но сохранить объект не
            выйдет. Право выдаёт администратор.
          </span>
        </Alert.Root>
      )}

      <SelectField
        id='new-site-customer'
        label='Заказчик'
        required
        value={customerId}
        onChange={(value) => {
          setCustomerId(value);
          // Договор принадлежит заказчику: сменили заказчика — прежний договор не годится.
          setContractId(NO_VALUE);
        }}
        options={(customers.data?.items ?? []).map((item) => ({
          value: item.id,
          label: item.name,
        }))}
        placeholder='Выберите заказчика'
        pending={customers.isPending}
        failed={customers.isError}
        failedError={customers.error as ApiError | null}
        emptyNote='Заказчиков нет ни одного. Заведите заказчика в представлении «Заказчики» — без него объект не создать.'
        error={errors.customer_id}
      />

      <SelectField
        id='new-site-legal-entity'
        label='Юрлицо'
        required
        value={legalEntityId}
        onChange={setLegalEntityId}
        options={(legalEntities.data?.items ?? []).map((item) => ({
          value: item.id,
          label: item.short_name ?? item.name,
        }))}
        placeholder='Выберите юрлицо'
        pending={legalEntities.isPending}
        failed={legalEntities.isError}
        failedError={legalEntities.error as ApiError | null}
        emptyNote='Юрлиц в справочнике нет. Их заводит администратор в настройках системы.'
        error={errors.legal_entity_id}
      />

      <SelectField
        id='new-site-type'
        label='Тип объекта'
        value={siteTypeId}
        onChange={setSiteTypeId}
        options={(siteTypes.data?.items ?? []).map((item) => ({
          value: item.id,
          label: item.title,
        }))}
        noneLabel='Тип не задан'
        placeholder='Выберите тип объекта'
        pending={siteTypes.isPending}
        failed={siteTypes.isError}
        failedError={siteTypes.error as ApiError | null}
        emptyNote='Типов объектов пока нет — объект заведётся без типа, шаблон ведомости придётся назначить ему отдельно.'
        error={errors.site_type_id}
        hint='От типа объект наследует шаблон ведомости, если своего у него нет.'
      />

      <div className='flex flex-col gap-1'>
        <Label.Root htmlFor='new-site-name'>
          Название
          <Label.Asterisk />
        </Label.Root>
        <Input.Root size='small' hasError={errors.name !== undefined}>
          <Input.Wrapper>
            <Input.Icon as={RiBuilding4Line} />
            <Input.Input
              id='new-site-name'
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder='Например: Шлюмберже, база'
              aria-invalid={errors.name !== undefined}
            />
          </Input.Wrapper>
        </Input.Root>
        {errors.name !== undefined && (
          <Hint.Root hasError>
            <Hint.Icon as={RiErrorWarningLine} />
            {errors.name}
          </Hint.Root>
        )}
      </div>

      <div className='grid grid-cols-2 gap-2'>
        <TextField
          id='new-site-city'
          label='Город'
          value={city}
          onChange={setCity}
          placeholder='Сургут'
          hint='По городу собираются отчёты и подбирается резерв.'
        />
        <TextField
          id='new-site-region'
          label='Регион'
          value={region}
          onChange={setRegion}
          placeholder='ХМАО'
        />
      </div>

      <TextField
        id='new-site-address'
        label='Адрес'
        value={address}
        onChange={setAddress}
        placeholder='ул. Промышленная, 4, корпус 2'
        hint='Куда ехать заступающему. Можно заполнить позже.'
      />

      <ВыборСтаршего
        id='new-site-manager'
        значение={fieldManager}
        наВыбор={setFieldManager}
      />

      <ЗонаНаКарте
        префикс='new-site'
        место={mapPoint}
        радиус={radius}
        область={polygon}
        обход={patrol}
        наМесто={setMapPoint}
        наРадиус={setRadius}
        наОбласть={setPolygon}
        наОбход={setPatrol}
      />

      <SelectField
        id='new-site-timezone'
        label='Часовой пояс объекта'
        required
        value={timezone}
        onChange={setTimezone}
        options={SITE_TIMEZONES.map((zone) => ({ value: zone.id, label: zone.title }))}
        placeholder='Выберите пояс'
        hint='Смена относится к дате заступления по поясу объекта, а не по московскому.'
      />

      <SelectField
        id='new-site-contract'
        label='Договор'
        value={contractId}
        onChange={setContractId}
        options={(contracts.data?.items ?? []).map((item) => ({
          value: item.id,
          label: item.number === null ? 'Договор без номера' : `№ ${item.number}`,
        }))}
        noneLabel='Без договора'
        placeholder={customerId === NO_VALUE ? 'Сначала выберите заказчика' : 'Выберите договор'}
        pending={customerId !== NO_VALUE && contracts.isPending}
        failed={contracts.isError}
        failedError={contracts.error as ApiError | null}
        emptyNote='У заказчика нет заведённых договоров — объект создастся без привязки к договору.'
        error={errors.contract_id}
      />

      {mutation.isError && <ErrorAlert error={mutation.error} />}

      {gateNote !== null && (
        <Hint.Root hasError>
          <Hint.Icon as={RiErrorWarningLine} />
          {gateNote}
        </Hint.Root>
      )}

      <div className='flex items-center gap-2 pt-1'>
        <Button.Root type='submit' variant='primary' mode='filled' size='small'>
          {mutation.isPending ? 'Создаём объект…' : 'Создать объект'}
        </Button.Root>
        <Button.Root type='button' variant='neutral' mode='stroke' size='small' onClick={onClose}>
          Отмена
        </Button.Root>
      </div>
    </form>
  );
}
